import React, { useCallback, useEffect, useState } from 'react';
import { accountsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Undo2, Ban } from 'lucide-react';

const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const errText = (e, f) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : d?.message || f; };

function ReasonAction({ label, testid, onConfirm, icon: Icon, tone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  if (!open) {
    return (
      <Button size="sm" variant="outline" className={`h-6 text-[11px] ${tone}`} onClick={() => setOpen(true)} data-testid={testid}>
        <Icon className="w-3 h-3 mr-1" /> {label}
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1">
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)"
        className="h-6 text-[11px] w-44 bg-zinc-800 border-zinc-700" data-testid={`${testid}-reason`} />
      <Button size="sm" className="h-6 text-[11px]" disabled={reason.trim().length < 5}
        onClick={() => onConfirm(reason.trim()).then(() => setOpen(false))} data-testid={`${testid}-confirm`}>Confirm</Button>
    </div>
  );
}

export default function ObligationDetailDialog({ obligationId, onClose, onChanged, canAct }) {
  const [o, setO] = useState(null);
  const load = useCallback(async () => {
    if (!obligationId) return;
    try { setO((await accountsAPI.getObligation(obligationId)).data); }
    catch (e) { toast.error(errText(e, 'Failed to load obligation')); }
  }, [obligationId]);
  useEffect(() => { setO(null); load(); }, [load]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); onChanged(); }
    catch (e) { toast.error(errText(e, 'Action failed')); throw e; }
  };

  return (
    <Dialog open={Boolean(obligationId)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl" data-testid="obligation-detail-dialog">
        <DialogHeader><DialogTitle>Obligation detail</DialogTitle></DialogHeader>
        {!o ? <Skeleton className="h-32" /> : (
          <div className="space-y-4 text-xs">
            <div className="grid grid-cols-3 gap-2">
              <div><div className="text-zinc-500">Net (liability)</div><div className="font-mono text-zinc-100">{inr(o.net_amount)}</div></div>
              <div><div className="text-zinc-500">Paid</div><div className="font-mono text-zinc-100">{inr(o.paid_amount)}</div></div>
              <div><div className="text-zinc-500">Remaining</div><div className="font-mono text-zinc-100" data-testid="detail-balance">{inr(o.balance_amount)}</div></div>
              <div><div className="text-zinc-500">Classification</div><div className="text-zinc-200" data-testid="detail-classification">{o.expense_classification || 'EXPENSE'}</div></div>
              <div><div className="text-zinc-500">Status</div><Badge className="text-[10px] bg-zinc-800 border-zinc-700" data-testid="detail-status">{o.display_status}</Badge></div>
              <div><div className="text-zinc-500">Due</div><div className="text-zinc-200">{o.due_date}</div></div>
            </div>
            <div>
              <div className="text-zinc-400 mb-1">Expense recognition (service period — independent of payment)</div>
              {o.recognition_schedule?.length ? (
                <div className="flex flex-wrap gap-1" data-testid="detail-recognition">
                  {o.recognition_schedule.map((r) => (
                    <span key={`${r.month}-${r.adjustment_id || r.kind}`} className="px-1.5 py-0.5 rounded border border-zinc-700 font-mono" data-testid={`detail-recognition-${r.month}-${r.kind}`}>{r.month}{r.kind === "ADJUSTMENT" ? " adj" : ""}: {inr(r.amount)}</span>
                  ))}
                </div>
              ) : <div className="text-zinc-500" data-testid="detail-recognition-none">No expense recognised (non-expense or draft).</div>}
            </div>
            <div>
              <div className="text-zinc-400 mb-1">Payment history (recordkeeping — not bank-verified)</div>
              {(o.payments || []).length === 0 && <div className="text-zinc-500" data-testid="detail-no-payments">No payments recorded.</div>}
              {(o.payments || []).map((p) => (
                <div key={p.payment_id || p.recorded_at} className="flex items-center gap-2 py-1 border-b border-zinc-800/60"
                  data-testid={`detail-payment-${p.payment_id}`}>
                  <span className="font-mono text-zinc-200">{inr(p.amount)}</span>
                  <span className="text-zinc-500">{p.payment_date}</span>
                  {p.reversed && <Badge className="text-[10px] bg-rose-500/15 text-rose-300 border-rose-500/30">Reversed: {p.reversal_reason}</Badge>}
                  {canAct && !p.reversed && p.payment_id && o.status !== 'CANCELLED' && (
                    <div className="ml-auto">
                      <ReasonAction label="Reverse" testid={`reverse-payment-${p.payment_id}`} icon={Undo2} tone="text-amber-300"
                        onConfirm={(reason) => act(() => accountsAPI.reverseObligationPayment(o.obligation_id, p.payment_id, reason), 'Payment reversed')} />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {canAct && o.status !== 'CANCELLED' && (
              <div className="flex items-center justify-between border-t border-zinc-800 pt-3">
                <span className="text-zinc-500">Cancelling writes off the remaining balance; history is kept.</span>
                <ReasonAction label="Cancel obligation" testid="cancel-obligation" icon={Ban} tone="text-rose-300"
                  onConfirm={(reason) => act(() => accountsAPI.cancelObligation(o.obligation_id, reason), 'Liability cancelled — recognised expense unchanged')} />
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
